/**
 * useShipmentNotifications Hook
 * 
 * React hook for managing shipment notifications with real-time updates
 * Shows unread count for sales reps when new shipments are assigned
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  onSnapshot,
  orderBy,
  Timestamp,
  writeBatch,
  doc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { ShipmentNotification } from '../@types/shipment';

interface UseShipmentNotificationsReturn {
  notifications: ShipmentNotification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  markAllAsRead: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
}

const NOTIFICATIONS_COLLECTION = 'shipment_notifications';

export const useShipmentNotifications = (): UseShipmentNotificationsReturn => {
  const { user, userRole } = useAuth();
  const [notifications, setNotifications] = useState<ShipmentNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch notifications for the current user
   */
  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Query unread notifications for this user
      const q = query(
        collection(db, NOTIFICATIONS_COLLECTION),
        where('sentTo', '==', user.id),
        where('read', '==', false)
      );

      const snapshot = await getDocs(q);
      const fetchedNotifications: ShipmentNotification[] = [];

      snapshot.forEach((doc) => {
        fetchedNotifications.push({
          id: doc.id,
          ...doc.data()
        } as ShipmentNotification);
      });

      // Sort by sentAt descending
      fetchedNotifications.sort((a, b) => {
        const timeA = a.sentAt?.toMillis?.() || 0;
        const timeB = b.sentAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setNotifications(fetchedNotifications);
      setUnreadCount(fetchedNotifications.length);
    } catch (err: any) {
      console.error('[useShipmentNotifications] Error fetching notifications:', err);
      setError(err.message || 'Failed to fetch notifications');
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!user || notifications.length === 0) {
      return;
    }

    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();

      for (const notification of notifications) {
        const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notification.id);
        batch.update(notificationRef, {
          read: true,
          readAt: now
        });
      }

      await batch.commit();
      
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
      
      console.log('[useShipmentNotifications] Marked', notifications.length, 'notifications as read');
    } catch (err: any) {
      console.error('[useShipmentNotifications] Error marking notifications as read:', err);
    }
  }, [user, notifications]);

  /**
   * Mark a single notification as read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) {
      return;
    }

    try {
      const notificationRef = doc(db, NOTIFICATIONS_COLLECTION, notificationId);
      const batch = writeBatch(db);
      batch.update(notificationRef, {
        read: true,
        readAt: Timestamp.now()
      });
      await batch.commit();

      // Update local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      console.error('[useShipmentNotifications] Error marking notification as read:', err);
    }
  }, [user]);

  /**
   * Refresh notifications
   */
  const refreshNotifications = useCallback(async () => {
    await fetchNotifications();
  }, [fetchNotifications]);

  // Initial fetch
  useEffect(() => {
    // Only fetch for sales reps
    if (userRole === 'sales-representative') {
      fetchNotifications();
    } else {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [fetchNotifications, userRole]);

  // Real-time subscription for sales reps
  useEffect(() => {
    if (!user || userRole !== 'sales-representative') {
      return;
    }

    const q = query(
      collection(db, NOTIFICATIONS_COLLECTION),
      where('sentTo', '==', user.id),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedNotifications: ShipmentNotification[] = [];

      snapshot.forEach((doc) => {
        updatedNotifications.push({
          id: doc.id,
          ...doc.data()
        } as ShipmentNotification);
      });

      // Sort by sentAt descending
      updatedNotifications.sort((a, b) => {
        const timeA = a.sentAt?.toMillis?.() || 0;
        const timeB = b.sentAt?.toMillis?.() || 0;
        return timeB - timeA;
      });

      setNotifications(updatedNotifications);
      setUnreadCount(updatedNotifications.length);
    }, (err) => {
      console.error('[useShipmentNotifications] Subscription error:', err);
    });

    return () => {
      unsubscribe();
    };
  }, [user, userRole]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAllAsRead,
    markAsRead,
    refreshNotifications
  };
};
